import { Goal } from '../../../Models/CarePlans/Goal.model';
import { LinkableItemType } from './types';
import { ITEM_CONFIGS } from './constants';
import { IntegratedCarePlan } from '../../../Models/CarePlans/IntegratedCarePlan.model';
import { CarePlanEnum } from '../../../Enum/CarePlanEnum';
import { isAfter, isValid } from 'date-fns';
import { toDate } from '../../../utils/dateHelpers';

export const isCarePlanReviewCurrent = (carePlan: IntegratedCarePlan, reviewedOn?: string | null): boolean => {
    const reviewDate = toDate(
        reviewedOn || carePlan.IsReviewedDate || carePlan.ReviewHistoryMember?.find((review) => Boolean(review.Signature))?.LastReviewedOn
    );
    if (!reviewDate || !isValid(reviewDate) || !carePlan.Goals?.length) return false;

    return carePlan.Goals.every((goal) =>
        [goal.CreatedAt, goal.UpdatedAt, goal.DeletedDate].every((timestamp) => {
            if (!timestamp) return true;
            const changedDate = toDate(timestamp);
            return Boolean(changedDate && isValid(changedDate) && !isAfter(changedDate, reviewDate));
        })
    );
};

/**
 * Utility functions for CarePlanUpdateTwo component
 */

/**
 * Gets related items for a goal by type using LinkedItems
 * @param goal - The goal to get related items for
 * @param type - The type of related items to retrieve
 * @param allItems - Object containing all item arrays keyed by type
 * @returns Array of related items of the specified type
 */
export const getRelatedItems = (goal: Goal, type: LinkableItemType, allItems: Record<string, any[]>): any[] => {
    const config = ITEM_CONFIGS[type];
    const source = allItems[config.key as string] || [];
    return source.filter((item: any) => goal.LinkedItems?.includes(item.Id));
};

export const getNewProgressModalState = () => ({
    open: false,
    goalId: null,
    progressId: null,
    value: '',
    type: null,
});

export const getRelated = (
    goal: Goal,
    type: 'strength' | 'barrier' | 'nextStep' | 'plan' | 'metric' | 'schedulePlan',
    integratedCarePlan: IntegratedCarePlan
) => {
    let source = [];
    switch (type) {
        case 'strength':
            source = integratedCarePlan.Strengths || [];
            break;
        case 'barrier':
            source = integratedCarePlan.Barriers || [];
            break;
        case 'nextStep':
            source = integratedCarePlan.NextSteps || [];
            break;
        case 'plan':
            source = integratedCarePlan.Plans || [];
            break;
        case 'metric':
            source = integratedCarePlan.Metrics || [];
            break;
        case 'schedulePlan':
            source = integratedCarePlan.SchedulePlans || [];
            break;
        default:
            source = [];
    }
    // Only include items whose Id is in goal.LinkedItems
    return source.filter((item) => goal.LinkedItems?.includes(item.Id));
};

/**
 * Formats the updated timestamp for display
 * @param item - Item with potential UpdatedAt timestamp
 * @returns Formatted timestamp string or empty string
 */
export const formatUpdatedTime = (item: any): string => {
    const date = item?.UpdatedAt;
    return date ? ` (Updated: ${new Date(date).toLocaleString()})` : '';
};

export const statusColor = (status: CarePlanEnum) => {
    switch (status) {
        case CarePlanEnum.NotStarted:
            return 'default';
        case CarePlanEnum.InProgress:
            return 'warning';
        case CarePlanEnum.Open:
            return 'secondary';
        case CarePlanEnum.Achieved:
            return 'success';
        case CarePlanEnum.NotMet:
            return 'default';
        case CarePlanEnum.Deleted:
            return 'error';
        default:
            return 'default';
    }
};
